import { CheckCircleIcon, LayersIcon, RouterIcon, ServerIcon } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { InfraStatus } from '../hooks/useInfraStatus'
import CreateVPCModal from './CreateVPCModal'

type InfraSetupBannerProps = {
  infraStatus: InfraStatus
}

export default function InfraSetupBanner({ infraStatus }: InfraSetupBannerProps) {
  const [showCreateVPC, setShowCreateVPC] = useState(false)

  // Nao renderiza se VPC já existe
  if (infraStatus.isReady || infraStatus.isLoading) return null

  const steps = [
    {
      label: 'Rede Privada (VPC)',
      icon: LayersIcon,
      done: infraStatus.hasVpc,
      enabled: true,
    },
    {
      label: 'Gateway',
      icon: RouterIcon,
      done: infraStatus.hasGateway,
      optional: true,
      enabled: infraStatus.hasVpc,
    },
    {
      label: 'Criar VM',
      icon: ServerIcon,
      done: false,
      enabled: infraStatus.hasVpc,
    },
  ]

  return (
    <>
      <div className="mb-6 rounded-xl border border-purple-200/60 bg-linear-to-r from-purple-50 to-indigo-50 p-5">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-purple-900">Configure sua infraestrutura</h3>
          <p className="mt-0.5 text-xs text-purple-700/80">
            Crie uma VPC para conectar suas maquinas virtuais em rede privada.
          </p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {steps.map((step, i) => (
            <div key={step.label} className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
              {/* Step card */}
              <div
                className={`flex min-w-0 flex-1 items-center gap-2 rounded-lg border px-3 py-2.5 sm:gap-3 ${
                  step.done
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : step.enabled
                      ? 'border-purple-200 bg-white text-purple-900'
                      : 'border-gray-200 bg-gray-50 text-gray-400'
                }`}
              >
                {step.done ? (
                  <CheckCircleIcon className="size-4 shrink-0 text-green-600" />
                ) : (
                  <step.icon
                    className={`size-4 shrink-0 ${step.enabled ? 'text-purple-500' : 'text-gray-300'}`}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-medium">{step.label}</div>
                  {step.optional && (
                    <span className="text-[10px] font-normal text-gray-500">(Opcional)</span>
                  )}
                  {/* Action */}
                  {!step.done && step.enabled && i === 0 && (
                    <button
                      onClick={() => setShowCreateVPC(true)}
                      className="mt-0.5 block text-[11px] font-semibold text-purple-600 hover:text-purple-800"
                    >
                      Criar VPC
                    </button>
                  )}
                  {!step.done && step.enabled && i === 1 && (
                    <Link
                      to="/gateway"
                      className="mt-0.5 block text-[11px] font-semibold text-indigo-600 hover:text-indigo-800"
                    >
                      Configurar
                    </Link>
                  )}
                  {!step.done && step.enabled && i === 2 && (
                    <Link
                      to="/marketplace"
                      className="mt-0.5 block text-[11px] font-semibold text-emerald-600 hover:text-emerald-800"
                    >
                      Ir ao Marketplace
                    </Link>
                  )}
                </div>
              </div>

              {/* Connector line */}
              {i < steps.length - 1 && (
                <div
                  className={`hidden h-px w-6 sm:block ${step.done ? 'bg-green-300' : 'bg-gray-200'}`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <CreateVPCModal isOpen={showCreateVPC} onClose={() => setShowCreateVPC(false)} />
    </>
  )
}
